import ThreatProtectionPage from '@/features/threat-protection/ThreatProtectionPage';
import { DocumentWorkspace } from '@workspace/silk-ui';
import AppLayout from '@/components/layout/AppLayout';
import { usePortalTours } from '@/tours/portalTours';
import { useCommerceAccess } from '@/lib/useCommerceAccess';
import MyAccountServicesPage from '@/views/MyAccountServicesPage';
import MyAccountBillingPage from '@/views/MyAccountBillingPage';
import SettingsPage from '@/views/SettingsPage';
import TenantIdentityProvidersPage from '@/views/TenantIdentityProvidersPage';
import TenantUsersPage from '@/views/TenantUsersPage';
import AutomationPage from '@/views/AutomationPage';
import DeveloperConsolePage from '@/views/DeveloperConsolePage';
import NotificationsPage from '@/views/NotificationsPage';
import StatusCollectionPage from '@/views/status/StatusCollectionPage';
import StatusInventoryPage from '@/views/status/StatusInventoryPage';
import PublicStatusMapSettingsPage from '@/views/status/PublicStatusMapSettingsPage';
import SalesMapPage from '@/views/SalesMapPage';
import SalesLeadsPage from '@/views/SalesLeadsPage';
import SalesDataSetupPage from '@/views/SalesDataSetupPage';
import SalesDataEditorPage from '@/views/SalesDataEditorPage';
import MapSourcesPage from '@/views/MapSourcesPage';
import MapStylesPage from '@/views/MapStylesPage';
import MapCustomMapsPage from '@/views/MapCustomMapsPage';
import MapSpritesPage from '@/views/MapSpritesPage';
import MapGlyphsPage from '@/views/MapGlyphsPage';
import AccountOverviewPage from '@/views/AccountOverviewPage';
import LegacyAccountRedirectPage from '@/views/LegacyAccountRedirectPage';
import MapStyleWorkbenchPage from '@/views/MapStyleWorkbenchPage';
import StorefrontsPage from '@/views/commerce/StorefrontsPage';
import MarketsPage from '@/views/commerce/MarketsPage';
import AccessPointsPage from '@/views/commerce/AccessPointsPage';
import CommerceEntitlementsPage from '@/views/commerce/CommerceEntitlementsPage';
import LinkedResellersPage from '@/views/commerce/LinkedResellersPage';
import LinkedSellersPage from '@/views/commerce/LinkedSellersPage';
import { CommerceFeatureUnavailable } from '@/views/commerce/CommerceFeatureUnavailable';
import PricebooksPage from '@/views/commerce/PricebooksPage';
import AssignmentsPage from '@/views/commerce/AssignmentsPage';
import CatalogsPage from '@/views/commerce/CatalogsPage';
import CatalogProductsPage from '@/views/commerce/CatalogProductsPage';
import PublicationsPage from '@/views/commerce/PublicationsPage';
import ResellerEntitlementsPage from '@/views/commerce/ResellerEntitlementsPage';
import CommissionPlansPage from '@/views/commerce/CommissionPlansPage';
import CommissionLedgerPage from '@/views/commerce/CommissionLedgerPage';
import DiscountsPage from '@/views/commerce/DiscountsPage';
import CartsPage from '@/views/commerce/CartsPage';
import QuotesPage from '@/views/commerce/QuotesPage';
import SalesRequestWizardPage from '@/views/commerce/SalesRequestWizardPage';
import OrdersPage from '@/views/commerce/OrdersPage';
import ProductVariantsPage from '@/views/commerce/ProductVariantsPage';
import PaymentProvidersPage from '@/views/payments/PaymentProvidersPage';
import PortalHelpCenterPage from '@/views/help/PortalHelpCenterPage';
import PortalHelpArticlePage from '@/views/help/PortalHelpArticlePage';
import PortalHelpOverlay from '@/views/help/PortalHelpOverlay';
import PortalLandingRedirectPage from '@/views/PortalLandingRedirectPage';
import PortalHomePage from '@/views/PortalHomePage';
import PathRedirectPage from '@/views/PathRedirectPage';
import SetupPage from '@/views/SetupPage';
import SupportPage from '@/views/SupportPage';
import { useNavigation } from '@/context/NavigationContext';
import { useSession } from '@/context/SessionContext';
import { parseAccountRoute } from '@/lib/account-routing';
import {
  isOwnerCommerceRoute,
  isResellerCommerceRoute,
  parseCommerceCatalogRoute,
  parseCommerceHelpRoute,
  parseEligibilityRoute,
  parsePricebookRoute,
} from '@/lib/commerce-routing';
import {
  buildPortalHelpOverlayRoute,
  buildPortalHelpRoute,
  clearPortalHelpOverlayRoute,
  getPortalHelpOverlayArticleId,
  parsePortalHelpRoute,
} from '@/lib/portal-help';

interface AppRoutesProps {
  path: string;
}

export default function AppRoutes({ path }: AppRoutesProps) {
  const normalizedPath = path.split('?')[0] || '/';
  const navigation = useNavigation();
  const { session } = useSession();
  const commerceAccess = useCommerceAccess();
  usePortalTours();

  let content: JSX.Element;
  const legacyAccountMatch = normalizedPath === '/account' || normalizedPath.startsWith('/account/');
  const accountRoute = parseAccountRoute(normalizedPath);
  const commerceCatalogRoute = parseCommerceCatalogRoute(normalizedPath);
  const commerceHelpRoute = parseCommerceHelpRoute(normalizedPath);
  const portalHelpRoute = parsePortalHelpRoute(normalizedPath);
  const eligibilityRoute = parseEligibilityRoute(normalizedPath);
  const pricebookRoute = parsePricebookRoute(normalizedPath);
  const portalHelpOverlayArticleId = getPortalHelpOverlayArticleId(path);
  const shouldGateCommerceRoutes = Boolean(session && commerceAccess.featureTenantId);

  if (shouldGateCommerceRoutes && isResellerCommerceRoute(normalizedPath) && !commerceAccess.resellerFeatureEnabled) {
    content = <CommerceFeatureUnavailable workspace="reseller" />;
  } else if (shouldGateCommerceRoutes && isOwnerCommerceRoute(normalizedPath) && !commerceAccess.commerceFeatureEnabled) {
    content = <CommerceFeatureUnavailable workspace="catalog-owner" />;
  } else if (legacyAccountMatch) {
    content = <LegacyAccountRedirectPage />;
  } else if (accountRoute.accountId && accountRoute.section === 'tickets' && accountRoute.ticketId) {
    content = <PathRedirectPage target="/support" />;
  } else if (accountRoute.accountId && accountRoute.section === 'services') {
    content = <DocumentWorkspace key={normalizedPath}><MyAccountServicesPage /></DocumentWorkspace>;
  } else if (accountRoute.accountId && accountRoute.section === 'tickets') {
    content = <PathRedirectPage target="/support" />;
  } else if (accountRoute.accountId && accountRoute.section === 'billing') {
    content = <DocumentWorkspace key={normalizedPath}><MyAccountBillingPage /></DocumentWorkspace>;
  } else if (accountRoute.accountId) {
    content = <DocumentWorkspace key={normalizedPath}><AccountOverviewPage /></DocumentWorkspace>;
  } else if (normalizedPath.startsWith('/maps/custom-maps/') && normalizedPath.endsWith('/workbench')) {
    const styleId = decodeURIComponent(normalizedPath.split('/')[3] ?? '');
    content = <MapStyleWorkbenchPage styleId={styleId} assetMode="custom-map" />;
  } else if (normalizedPath === '/maps/custom-maps/workbench' || normalizedPath === '/maps/custom-maps/workbench/') {
    content = <MapStyleWorkbenchPage assetMode="custom-map" />;
  } else if (normalizedPath.startsWith('/maps/styles/') && normalizedPath.endsWith('/workbench')) {
    const styleId = decodeURIComponent(normalizedPath.split('/')[3] ?? '');
    content = <MapStyleWorkbenchPage styleId={styleId} />;
  } else if (normalizedPath === '/maps/workbench' || normalizedPath === '/maps/workbench/') {
    content = <MapStyleWorkbenchPage />;
  } else if (commerceCatalogRoute?.kind === 'catalogs') {
    content = <CatalogsPage />;
  } else if (commerceCatalogRoute?.kind === 'catalog') {
    content = <CatalogProductsPage catalogId={commerceCatalogRoute.catalogId} />;
  } else if (commerceCatalogRoute?.kind === 'product') {
    content = (
      <ProductVariantsPage
        catalogId={commerceCatalogRoute.catalogId}
        productId={commerceCatalogRoute.productId}
      />
    );
  } else if (portalHelpRoute?.kind === 'help' && portalHelpRoute.articleId) {
    content = <DocumentWorkspace key={normalizedPath}><PortalHelpArticlePage articleId={portalHelpRoute.articleId} /></DocumentWorkspace>;
  } else if (portalHelpRoute?.kind === 'help') {
    content = <DocumentWorkspace key={normalizedPath}><PortalHelpCenterPage /></DocumentWorkspace>;
  } else if (commerceHelpRoute?.kind === 'help') {
    content = <PathRedirectPage target={buildPortalHelpRoute(`commerce-${commerceHelpRoute.sectionId}`)} />;
  } else if (eligibilityRoute?.kind === 'eligibility') {
    content = <PathRedirectPage target="/commerce/storefronts" />;
  } else if (pricebookRoute?.kind === 'pricebooks') {
    content = <PricebooksPage />;
  } else {
    switch (normalizedPath) {
      case '/':
        content = <PathRedirectPage target="/dashboard" />;
        break;
      case '/accounts':
      case '/accounts/':
        content = <PortalLandingRedirectPage />;
        break;
      case '/threat-protection':
        content = <PathRedirectPage target="/threat-protection/overview" />;
        break;
      case '/threat-protection/overview':
      case '/threat-protection/overview/':
        content = <ThreatProtectionPage section="overview" />;
        break;
      case '/threat-protection/ips':
      case '/threat-protection/ips/':
        content = <ThreatProtectionPage section="ips" />;
        break;
      case '/threat-protection/bans':
      case '/threat-protection/bans/':
        content = <ThreatProtectionPage section="bans" />;
        break;
      case '/threat-protection/sources':
      case '/threat-protection/sources/':
        content = <ThreatProtectionPage section="sources" />;
        break;
      case '/threat-protection/allowlist':
      case '/threat-protection/allowlist/':
        content = <ThreatProtectionPage section="allowlist" />;
        break;
      case '/dashboard':
      case '/dashboard/':
        content = <DocumentWorkspace key={normalizedPath}><PortalHomePage /></DocumentWorkspace>;
        break;
      case '/start':
      case '/start/':
        content = <PathRedirectPage target="/dashboard" />;
        break;
      case '/sales':
      case '/sales/':
        content = <SalesMapPage />;
        break;
      case '/sales/leads':
      case '/sales/leads/':
        content = <SalesLeadsPage />;
        break;
      case '/sales/setup':
      case '/sales/setup/':
        content = <PathRedirectPage target="/commerce/data-setup" />;
        break;
      case '/commerce/data-setup':
      case '/commerce/data-setup/':
        content = <SalesDataEditorPage />;
        break;
      case '/my-info':
      case '/user':
        content = <PathRedirectPage target="/settings" />;
        break;
      case '/settings':
        content = <SettingsPage />;
        break;
      case '/settings/identity':
      case '/settings/identity/':
        content = <TenantIdentityProvidersPage />;
        break;
      case '/support':
      case '/support/':
        content = <DocumentWorkspace key={normalizedPath}><SupportPage /></DocumentWorkspace>;
        break;
      case '/users':
      case '/users/':
        content = <PathRedirectPage target="/users/active" />;
        break;
      case '/users/active':
      case '/users/active/':
        content = <TenantUsersPage section="active" />;
        break;
      case '/users/invites':
      case '/users/invites/':
        content = <TenantUsersPage section="invites" />;
        break;
      case '/setup':
      case '/setup/':
        content = <DocumentWorkspace key={normalizedPath}><SetupPage /></DocumentWorkspace>;
        break;
      case '/join/member':
      case '/join/member/':
        content = <PathRedirectPage target="/dashboard" />;
        break;
      case '/join/partner':
      case '/join/partner/':
        content = <PathRedirectPage target="/dashboard" />;
        break;
      case '/help':
      case '/help/':
        content = <DocumentWorkspace key={normalizedPath}><PortalHelpCenterPage /></DocumentWorkspace>;
        break;
      case '/notifications':
      case '/notifications/':
        content = <PathRedirectPage target="/notifications/channels" />;
        break;
      case '/notifications/channels':
      case '/notifications/channels/':
        content = <NotificationsPage section="channels" />;
        break;
      case '/notifications/recipients':
      case '/notifications/recipients/':
        content = <NotificationsPage section="recipients" />;
        break;
      case '/notifications/routes':
      case '/notifications/routes/':
        content = <NotificationsPage section="routes" />;
        break;
      case '/notifications/activity':
      case '/notifications/activity/':
        content = <NotificationsPage section="activity" />;
        break;
      case '/payments':
      case '/payments/':
        content = <PathRedirectPage target="/payments/providers" />;
        break;
      case '/payments/providers':
      case '/payments/providers/':
        content = <PaymentProvidersPage />;
        break;
      case '/api-gateway':
      case '/api-gateway/':
        content = <PathRedirectPage target="/api-gateway/access" />;
        break;
      case '/api-access':
      case '/api-access/':
      case '/api-gateway/keys':
      case '/api-gateway/keys/':
        content = <PathRedirectPage target="/api-gateway/access" />;
        break;
      case '/api-gateway/access':
      case '/api-gateway/access/':
        content = <AutomationPage />;
        break;
      case '/api-gateway/console':
      case '/api-gateway/console/':
        content = <DeveloperConsolePage />;
        break;
      case '/status/operations/collection':
      case '/status/operations/collection/':
        content = <StatusCollectionPage />;
        break;
      case '/status':
      case '/status/':
      case '/status/inventory':
      case '/status/inventory/':
        content = <PathRedirectPage target="/status/inventory/devices" />;
        break;
      case '/status/inventory/hubs':
      case '/status/inventory/hubs/':
      case '/status/inventory/devices':
      case '/status/inventory/devices/':
      case '/status/inventory/links':
      case '/status/inventory/links/':
      case '/status/inventory/models':
      case '/status/inventory/models/':
      case '/status/inventory/auth':
      case '/status/inventory/auth/':
      case '/status/inventory/overlays':
      case '/status/inventory/overlays/':
      case '/status/inventory/entities':
      case '/status/inventory/entities/':
        content = <StatusInventoryPage />;
        break;
      case '/status/public-map':
      case '/status/public-map/':
        content = <PublicStatusMapSettingsPage />;
        break;
      case '/debug/session':
        content = <PathRedirectPage target="/settings" />;
        break;
      case '/commerce':
      case '/commerce/':
        content = <PathRedirectPage target="/commerce/catalogs" />;
        break;
      case '/commerce/publications':
      case '/commerce/publications/':
        content = <PublicationsPage />;
        break;
      case '/commerce/storefronts':
      case '/commerce/storefronts/':
        content = <StorefrontsPage />;
        break;
      case '/commerce/channels':
      case '/commerce/channels/':
        content = <PathRedirectPage target="/commerce/storefronts" />;
        break;
      case '/commerce/markets':
      case '/commerce/markets/':
        content = <MarketsPage />;
        break;
      case '/commerce/access-points':
      case '/commerce/access-points/':
        content = <AccessPointsPage />;
        break;
      case '/commerce/eligibility':
      case '/commerce/eligibility/':
        content = <PathRedirectPage target="/commerce/storefronts" />;
        break;
      case '/commerce/entitlements':
      case '/commerce/entitlements/':
        content = <CommerceEntitlementsPage />;
        break;
      case '/commerce/linked-resellers':
      case '/commerce/linked-resellers/':
        content = <LinkedResellersPage />;
        break;
      case '/reseller/entitlements':
      case '/reseller/entitlements/':
        content = <ResellerEntitlementsPage />;
        break;
      case '/reseller/linked-sellers':
      case '/reseller/linked-sellers/':
        content = <LinkedSellersPage />;
        break;
      case '/commerce/pricebooks':
      case '/commerce/pricebooks/':
        content = <PricebooksPage />;
        break;
      case '/commerce/assignments':
      case '/commerce/assignments/':
        content = <AssignmentsPage />;
        break;
      case '/commerce/commission-plans':
      case '/commerce/commission-plans/':
        content = <CommissionPlansPage />;
        break;
      case '/commerce/commission-ledger':
      case '/commerce/commission-ledger/':
        content = <CommissionLedgerPage workspace="catalog-owner" />;
        break;
      case '/reseller/commission-ledger':
      case '/reseller/commission-ledger/':
        content = <CommissionLedgerPage workspace="reseller" />;
        break;
      case '/commerce/discounts':
      case '/commerce/discounts/':
        content = <DiscountsPage />;
        break;
      case '/commerce/carts':
      case '/commerce/carts/':
        content = <CartsPage workspace="catalog-owner" />;
        break;
      case '/reseller/carts':
      case '/reseller/carts/':
        content = <CartsPage workspace="reseller" />;
        break;
      case '/sales/requests/new':
      case '/sales/requests/new/':
        content = <SalesRequestWizardPage />;
        break;
      case '/commerce/quotes':
      case '/commerce/quotes/':
        content = <QuotesPage workspace="catalog-owner" />;
        break;
      case '/agent/quotes':
      case '/agent/quotes/':
      case '/sales/quotes':
      case '/sales/quotes/':
        content = <QuotesPage workspace="seller" />;
        break;
      case '/commerce/orders':
      case '/commerce/orders/':
        content = <OrdersPage workspace="catalog-owner" />;
        break;
      case '/reseller/orders':
      case '/reseller/orders/':
      case '/sales/orders':
      case '/sales/orders/':
        content = <OrdersPage workspace="reseller" />;
        break;
      case '/maps/sources':
      case '/maps/sources/':
        content = <MapSourcesPage />;
        break;
      case '/maps/styles':
      case '/maps/styles/':
        content = <DocumentWorkspace key={normalizedPath}><MapStylesPage /></DocumentWorkspace>;
        break;
      case '/maps/custom-maps':
      case '/maps/custom-maps/':
        content = <DocumentWorkspace key={normalizedPath}><MapCustomMapsPage /></DocumentWorkspace>;
        break;
      case '/maps/sprites':
      case '/maps/sprites/':
        content = <DocumentWorkspace key={normalizedPath}><MapSpritesPage /></DocumentWorkspace>;
        break;
      case '/maps/glyphs':
      case '/maps/glyphs/':
        content = <DocumentWorkspace key={normalizedPath}><MapGlyphsPage /></DocumentWorkspace>;
        break;
      default:
        content = <PortalLandingRedirectPage />;
        break;
    }
  }

  return (
    <AppLayout path={normalizedPath}>
      {content}
      {portalHelpOverlayArticleId ? (
        <PortalHelpOverlay
          articleId={portalHelpOverlayArticleId}
          onClose={() => navigation.navigate(clearPortalHelpOverlayRoute(path), { replace: true })}
          onSelectArticle={(articleId) =>
            navigation.navigate(buildPortalHelpOverlayRoute(articleId, path), { replace: true })
          }
          onOpenArticle={(articleId) => navigation.navigate(buildPortalHelpRoute(articleId))}
        />
      ) : null}
    </AppLayout>
  );
}
